import { useCustomerStore } from '../stores/customerStore'
import { useListStore } from '../stores/listStore'
import { useProductStore } from '../stores/productStore'
import type { BuyRequest, IRogue } from '@/js/services/RogueTypes'
import { getSimpleForBuyRequest } from './buy-request'

export type CompatibilityProduct = {
  productId?: number
  name: string
  sku: string
  tag: string[]
  type?: string
  src?: string
}

export type CompatibilityResult = {
  name: string;
  accessory: CompatibilityProduct & { accessoryGroup: string, listItemId?: string };
  compatible: CompatibilityProduct[];
  incompatible: CompatibilityProduct[];
};
export async function loadCompatibleAccessories(rogue: IRogue, tag: string[], accessoryGroup: string): Promise<CompatibilityProduct[]> {
  // This is an incompatible product - see if we can find a compatible version
  try {
    const query = new URLSearchParams()
    query.set('tag', tag[0] || '')
    query.set('group', accessoryGroup)
    const alternates = await rogue.api.fetch<{sku: string, productId: number, name: string}[]>({
      url: `/svc_content/catalog/product/compatibility?${query.toString()}`,
    })

    return alternates.map(alt => ({ sku: alt.sku, name: alt.name, tag, productId: alt.productId }))
  } catch (e) {
    rogue.log.warn('Error loading compatible results', e as Error)
  }

  return []
}

function convertTagToStringArray(tag: string | string[] | undefined): string[] {
  if (!tag) {
    return []
  }

  return (Array.isArray(tag) ? tag : [tag])
}

export function checkCompatibility(rogue: IRogue, item: BuyRequest): CompatibilityResult | null {
  // Find which items in cart have a compat_type, like 'rack'
  const accessory = getSimpleForBuyRequest(item, rogue)
  // compat_tag could be a string or an array of strings
  const tag = convertTagToStringArray(accessory?.attributes.compat_tag as string | string[])
  const type = accessory?.attributes.compat_accessory_for_type as string
  const accessoryGroup = accessory?.attributes.compat_group as string

  // If the product doesn't have a `compat_group`, then we do not need to check anything.
  // This also means its a NOOP in production until we start assigning compat_groups
  if (!accessory || !tag || !accessoryGroup) {
    return null
  }

  // We need to wait for the cart to load
  const customerStore = useCustomerStore(rogue.pinia)
  const listStore = useListStore(rogue.pinia)
  const productStore = useProductStore(rogue.pinia)

  // Find the "Base Items" that go with this accessory.
  // For example if this is a matador, then `matador.attributes.compat_accessory_for_type` === 'rack'
  // So we need to find all of the "compat_type === 'rack'" products in cart
  const baseItems = [...listStore.cart.items, ...customerStore.orders.filter(o => !o.isCancelled).flatMap(o => o.items)]
    .map(listItem => ({
      product : productStore.products[listItem.productId],
      simple  : getSimpleForBuyRequest(listItem, rogue),
      item    : listItem,
    }))
    .filter(productData => productData.simple && productData.simple.attributes.compat_type === accessory.attributes.compat_accessory_for_type)
    .map(productData => ({
      // @ts-ignore name property exists on order history items, despite not showing in ts definition
      name : productData.item.name || productData.product.name || productData.simple.name,
      sku  : productData.simple.sku,
      tag  : convertTagToStringArray(productData.simple.attributes.compat_tag as string | string[]),
      type : productData.simple.attributes.compat_type as string,
      src  : listStore.cart.items.map(i => i.productId).includes(productData.product.productId) ? 'cart' : 'order history',
    }))

  if (!baseItems.length) {
    // If the customer doesn't have anything relevant in cart, then we dont need to do anything for compatibility
    return null
  }

  const accessoryTagSet = new Set(tag)
  const retval : CompatibilityResult = {
    name      : accessoryGroup,
    accessory : {
      sku        : accessory.sku,
      name       : accessory.name,
      tag,
      type,
      accessoryGroup,
      listItemId : item.listItemId || item._id,
    },
    // Compatible accessories are where the tags match. Incompatible is where they dont.
    // @todo eventually this will be a more complex rules-based system, but for P1 its just checking the tag
    compatible   : baseItems.filter(b => b.tag.some(baseTagItem => accessoryTagSet.has(baseTagItem))),
    incompatible : baseItems.filter(b => !b.tag.some(baseTagItem => accessoryTagSet.has(baseTagItem))),
  }

  return retval
}
